import React, { useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { classNames } from 'primereact/utils';
import axiosInstance from '../../../config/axios.config';
import { useReportService, useInvalidateReportService } from '../../../hooks/queries/useReportService';
import GenericSurepassOutputComponent from '../output/GenericSurepassOutputComponent';
import ConsentCheckbox from './ConsentCheckbox';
import ReportBanner from '../ReportBanner';

const EsignGetAuditTrailInputComponent = () => {
    const { serviceId } = useParams();
    const toast = useRef(null);
    const submittingRef = useRef(false);

    const [formData, setFormData] = useState({ clientId: '', consent: false });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    // #161: one shared, cached query instead of a per-component fetch on every
    // mount. Concurrent consumers are deduped into a single request and a
    // revisit is served from cache; the balance is invalidated after a report
    // runs (below), which is what keeps a cached credit figure correct.
    const {
        data: fetchedServiceDetails,
        isFetching: fetchingService,
        isError: serviceError,
    } = useReportService(serviceId);
    const invalidateReportService = useInvalidateReportService();
    const serviceDetails = serviceError
        ? { available_credits: 'N/A', credits_required: 'N/A' }
        : (fetchedServiceDetails || null);

    const handleChange = (e) => {
        const value = (e.target.value || '').trim();
        setFormData((prev) => ({ ...prev, clientId: value }));
        if (errors.clientId) setErrors((prev) => ({ ...prev, clientId: '' }));
    };

    const handleConsentChange = (e) => {
        setFormData((prev) => ({ ...prev, consent: e.checked }));
        if (errors.consent) setErrors((prev) => ({ ...prev, consent: '' }));
    };

    const validate = () => {
        const next = {};
        if (!formData.clientId) next.clientId = 'Client ID is required';
        if (!formData.consent) next.consent = 'Please confirm consent before submitting';
        return next;
    };

    const submit = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.post('/surepass/esign/get-audit-trail', {
                client_id: formData.clientId,
                api_provider_service_id: parseInt(serviceId, 10),
            });
            if (!res.data.success) throw new Error(res.data.message || 'Audit trail fetch failed');
            setReportData(res.data);
            toast.current?.show({ severity: 'success', summary: 'Audit trail fetched', detail: 'Report ready below', life: 2000 });
            // Credits just changed. Invalidating updates every mounted consumer at
            // once and is ordered strictly after the deduction.
            invalidateReportService(serviceId);
        } catch (error) {
            console.error('Esign audit-trail failed:', error?.message || 'Unknown error');
            const status = error.response?.status;
            const map = {
                500: 'Internal server error. Please try again later.',
                422: 'Please check the client ID and try again.',
                429: 'Too many requests. Please wait a minute before trying again.',
                400: 'Invalid client ID. Please verify and retry.',
                404: 'No audit trail found for this client ID.',
            };
            let detail = map[status] || error.response?.data?.message || error.message || 'Audit trail fetch failed';
            if (status === 402) {
                const c = error.response?.data || {};
                detail = `Insufficient credits. Required: ${c.required_credits ?? 'N/A'}, Available: ${c.current_balance ?? 'N/A'}`;
            }
            toast.current?.show({ severity: 'error', summary: 'Audit trail failed', detail, life: 3500 });
        } finally {
            setLoading(false);
            submittingRef.current = false;
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (submittingRef.current) return;
        const next = validate();
        if (Object.keys(next).length > 0) {
            setErrors(next);
            toast.current?.show({ severity: 'warn', summary: 'Validation error', detail: 'Please fix the highlighted fields', life: 3000 });
            return;
        }
        setErrors({});
        submittingRef.current = true;
        const required = serviceDetails?.credits_required;
        const available = serviceDetails?.available_credits;
        if (typeof available === 'number' && typeof required === 'number' && available < required) {
            submittingRef.current = false;
            toast.current?.show({ severity: 'error', summary: 'Insufficient credits', detail: `Required: ${required}, Available: ${available}`, life: 3500 });
            return;
        }
        confirmDialog({
            message: (
                <div className="text-sm">
                    <div className="mb-2"><span className="text-600">Client ID:</span> <span className="text-900 font-medium">{formData.clientId}</span></div>
                    <div className="mb-2"><span className="text-600">Available credits:</span> <span className="text-900 font-medium">{available ?? 'N/A'}</span></div>
                    <div><span className="text-600">Credits to deduct:</span> <span className="text-900 font-medium">{required ?? 'N/A'}</span></div>
                </div>
            ),
            header: 'Confirm audit trail fetch',
            icon: 'pi pi-question-circle text-primary pi-2x',
            acceptLabel: 'Yes, fetch',
            rejectLabel: 'Cancel',
            accept: submit,
            reject: () => { submittingRef.current = false; },
            style: { width: '90vw', maxWidth: '450px' },
            draggable: false,
        });
    };

    const handleAnother = () => {
        setReportData(null);
        setFormData({ clientId: '', consent: false });
        setErrors({});
    };

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />
            <div className="grid">
                <div className="col-12">
                    <ReportBanner
                        icon="pi-history"
                        title="E-Sign Audit Trail"
                        subtitle="Retrieve the audit trail for an e-sign request"
                        availableCredits={serviceDetails?.available_credits}
                        requiredCredits={serviceDetails?.credits_required}
                        loading={fetchingService}
                    />
                    {!reportData ? (
                        <Card>
                            <form onSubmit={handleSubmit} noValidate>
                                <div className="grid">
                                    <div className="field col-12 md:col-8 lg:col-6">
                                        <label htmlFor="esignAuditId" className="block font-medium text-sm mb-2">
                                            Client ID <span className="text-red-500">*</span>
                                        </label>
                                        <IconField iconPosition="left" className="w-full">
                                            <InputIcon className="pi pi-key" />
                                            <InputText
                                                id="esignAuditId"
                                                value={formData.clientId}
                                                onChange={handleChange}
                                                placeholder="esign_xxxxxxxxxxxxx"
                                                disabled={loading}
                                                className={classNames('w-full p-inputtext-sm font-mono', { 'p-invalid': !!errors.clientId })}
                                                aria-describedby="esignAuditId-help"
                                                aria-invalid={!!errors.clientId}
                                            />
                                        </IconField>
                                        {errors.clientId && (
                                            <small id="esignAuditId-help" className="p-error block mt-1" role="alert">
                                                <i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />
                                                {errors.clientId}
                                            </small>
                                        )}
                                    </div>
                                </div>
                                <ConsentCheckbox checked={formData.consent} onChange={handleConsentChange} error={errors.consent} reportType="esign-get-audit-trail" />
                                <div className="flex justify-content-end mt-3">
                                    <Button type="submit" label="Fetch Audit Trail" icon="pi pi-arrow-right" iconPos="right" loading={loading} disabled={loading} size="small" />
                                </div>
                            </form>
                        </Card>
                    ) : (
                        <>
                            <div className="flex justify-content-end mb-3">
                                <Button label="Fetch another" icon="pi pi-refresh" severity="secondary" outlined size="small" onClick={handleAnother} />
                            </div>
                            <GenericSurepassOutputComponent reportData={reportData} title="E-Sign Audit Trail" icon="pi-history" />
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default EsignGetAuditTrailInputComponent;
